import apiConstants from '@/constants/api.constants';
import MIDDLEWARE_CONSTANTS from '@/constants/middleware.constants';
import { AuthMiddlewareRedirect, UserDTO } from '@/types/other';
import { getCookie, hasCookie, setCookie } from 'cookies-next';
import { NextRequest, NextResponse } from 'next/server';
import cookiesConstants from '@/constants/cookies.constants';
import envPublic from '@/config/env.public';
import { FetchClient } from '@/lib/node-fetch/fetchClient';
import { BaseApiResponse, SessionResponse } from '@/types/api/api-v2';
function handleAuthRedirect(authRes: AuthMiddlewareRedirect, req: NextRequest) {
  const ip = req.headers.get('x-forwarded-for')?.split(',')[0] ?? 'N/A';
  const response = NextResponse.redirect(authRes.url);
  if (authRes.extra.cookies.length > 0) {
    authRes.extra.cookies.forEach(cookie => {
      setCookie(cookie.name, cookie.value, {
        req,
        res: response,
        ...cookie.options,
      });
    });
  }
  setCookie(cookiesConstants.COOKIES.IP.name, ip, { req, res: response });
  return response;
}

function handleAuthCookie(authRes: AuthMiddlewareRedirect, req: NextRequest) {
  const ip = req.headers.get('x-forwarded-for')?.split(',')[0] ?? 'N/A';

  const response = NextResponse.next();

  if (authRes.extra.cookies.length > 0) {
    authRes.extra.cookies.forEach(cookie => {
      setCookie(cookie.name, cookie.value, {
        req,
        res: response,
        ...cookie.options,
      });
    });
  }
  setCookie(cookiesConstants.COOKIES.IP.name, ip, { req, res: response });

  return response;
}

function handlethrowUserNotAdmin() {
  const response = NextResponse.error();
  return response;
}

function handleUserLangRedirect(userLangRes: AuthMiddlewareRedirect, req: NextRequest) {
  const response = NextResponse.redirect(userLangRes.url);
  const ip = req.headers.get('x-forwarded-for')?.split(',')[0] ?? 'N/A';
  if (userLangRes.extra.cookies.length > 0) {
    setCookie(cookiesConstants.COOKIES.IP.name, ip, { req, res: response });
    userLangRes.extra.cookies.forEach(cookie => {
      setCookie(cookie.name, cookie.value, {
        req,
        res: response,
        ...cookie.options,
      });
    });
  }
  setCookie(cookiesConstants.COOKIES.IP.name, ip, { req, res: response });

  return response;
}

function isProtectedPath(pathname: string): boolean {
  return MIDDLEWARE_CONSTANTS.PROTECTED_PATHS.some(protectedUrl => {
    if (pathname?.toLowerCase().startsWith('/sku')) {
      const segments = pathname?.split('/');
      const skuPath = segments[segments.length - 1];
      const isProtectedSku = MIDDLEWARE_CONSTANTS.SKU_PROTECTED_PATHS.some(skuUrl => {
        return skuPath.toLowerCase().startsWith(skuUrl.toLowerCase());
      });
      return isProtectedSku;
    }
    return pathname?.toLowerCase().startsWith(protectedUrl.toLowerCase());
  });
}

function isAdminPath(pathname: string): boolean {
  return MIDDLEWARE_CONSTANTS.ADMIN_PATHS.some(adminUrl => {
    return pathname?.toLowerCase().startsWith(adminUrl.toLowerCase());
  });
}

function isStaticFileOrInternalPath(pathname: string): boolean {
  return (
    MIDDLEWARE_CONSTANTS.STATIC_FILE_EXTENSIONS.test(pathname) ||
    MIDDLEWARE_CONSTANTS.INTERNAL_NEXTJS_PATHS.test(pathname) ||
    pathname?.startsWith('/_next') ||
    pathname?.includes('/api/') ||
    MIDDLEWARE_CONSTANTS.PUBLIC_FILE.test(pathname)
  );
}

function hasAuthTokenCookie(req: NextRequest): boolean {
  return hasCookie(cookiesConstants.COOKIES.AUTH_TOKENS.name, { req });
}
function getAuthTokenCookie(req: NextRequest) {
  try {
    const cookieValue = getCookie(cookiesConstants.COOKIES.AUTH_TOKENS.name, { req });

    if (!cookieValue) {
      return null;
    }

    return JSON.parse(cookieValue as string) as unknown as {
      accessToken: string;
      accessTokenExpiresAt: number;
      refreshToken: string;
    } | null;
  } catch (err) {
    return null;
  }
}

async function isUserAdmin(req: NextRequest) {
  const authTokens = getAuthTokenCookie(req);
  const access_token = authTokens?.accessToken ?? null;
  if (!access_token) {
    return false;
  }
  try {
    const { status } = await fetch(apiConstants.ENDPOINTS.BACKEND.BASE + '/admin', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
    if ((status >= 200 && status < 300) || status === 304) {
      return true;
    }
  } catch (err: unknown) {
    return false;
  }
}
async function isUserAuthenticated(req: NextRequest) {
  try {
    const authTokens = getAuthTokenCookie(req);
    const access_token = authTokens?.accessToken ?? null;
    if (!access_token) {
      return false;
    }

    const headers = {
      Authorization: `Bearer ${access_token}`,
      Cookie: req.headers.get('cookie') ?? '',
    } as unknown as HeadersInit;

    const fetchClient = new FetchClient(envPublic.NEXT_PUBLIC_BASE_URL);
    const user = await fetchClient.get('/api/get-user', { headers });

    const isAuthenticated = !!user && 'id' in user;
    return isAuthenticated;
  } catch (err: unknown) {
    return false;
  }
}

async function getUserMiddleware(req: NextRequest) {
  try {
    const authTokens = getAuthTokenCookie(req);
    const accessToken = authTokens?.accessToken ?? null;
    if (!accessToken) {
      return null;
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Cookie: req.headers.get('cookie') ?? '',
    } as unknown as HeadersInit;
    const fetchClient = new FetchClient(envPublic.NEXT_PUBLIC_BASE_URL);
    const user = await fetchClient.get<UserDTO>('/api/get-user', { headers });
    return user;
  } catch (err) {
    return null;
  }
}

/**
 * Retrieves and validates the session token for a request
 * Returns cookie data if a new session token needs to be set
 */
async function getSessionToken(req: NextRequest) {
  try {
    // Get current session token from cookies
    const currentSessionToken = getCookie(cookiesConstants.COOKIES.SESSION_TOKEN.name, { req });
    const cookieHeader = req.headers.get('cookie') ?? '';

    // Create fetch client once
    const fetchClient = new FetchClient(envPublic.NEXT_PUBLIC_BASE_URL);

    // Prepare common request options
    let requestOptions = {
      headers: {
        Cookie: cookieHeader,
        'x-forwarded-for': req.headers.get('x-forwarded-for'),
        'x-real-ip': req.headers.get('x-real-ip'),
      } as HeadersInit,
    };

    // Check if user is authenticated
    const user = await getUserMiddleware(req);

    // Add authorization header if user is authenticated
    if (user) {
      const authTokens = getAuthTokenCookie(req);
      const accessToken = authTokens?.accessToken;
      if (accessToken) {
        requestOptions = {
          ...requestOptions,
          headers: {
            ...requestOptions.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        };
      }
    }

    // Fetch session token from API
    const response = await fetchClient.get<BaseApiResponse<SessionResponse>>(
      '/api/auth/session',
      requestOptions
    );

    // Return cookie data if session token has changed
    if (response.data.sessionToken !== currentSessionToken) {
      return {
        setCookie: response.data.cookies,
      };
    }

    return null;
  } catch (err) {
    return null;
  }
}

function isTokenAboutToExpire(req: NextRequest): boolean {
  try {
    const authTokens = getAuthTokenCookie(req);
    const accessTokenExpiresAt = authTokens?.accessTokenExpiresAt ?? null;

    if (!accessTokenExpiresAt) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);

    const timeToExpire = accessTokenExpiresAt - currentTime;
    const isAboutToExpire = timeToExpire <= 300;

    return isAboutToExpire;
  } catch (err) {
    return false;
  }
}
export {
  getAuthTokenCookie,
  handleAuthCookie,
  handleAuthRedirect,
  handlethrowUserNotAdmin,
  handleUserLangRedirect,
  hasAuthTokenCookie,
  isAdminPath,
  isProtectedPath,
  isStaticFileOrInternalPath,
  isTokenAboutToExpire,
  isUserAdmin,
  isUserAuthenticated,
  getUserMiddleware,
  getSessionToken,
};
